// CB:BEGIN help-panel
import React, { useState } from 'react';
import { getArticle, getArticles, type HelpArticle } from '../help/articles';

interface HelpPanelProps {
  /** Slugs to show (from NextAction.helpArticles) */
  slugs?: string[];
  /** Optional inline trigger — if omitted, render full panel */
  triggerLabel?: string;
}

/**
 * HelpPanel – renders help articles inline or in a modal triggered by a button.
 * Used by NextActionBanner to surface contextual help.
 */
export const HelpPanel: React.FC<HelpPanelProps> = ({ slugs = [], triggerLabel }) => {
  const [open, setOpen] = useState(false);
  const [activeSlug, setActiveSlug] = useState<string | null>(slugs[0] ?? null);

  const articles = slugs.length > 0 ? getArticles(slugs) : [];

  if (articles.length === 0) return null;

  const active = activeSlug ? getArticle(activeSlug) : articles[0];

  const panelContent = (
    <div className="help-panel" style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
      {/* Sidebar nav for multiple articles */}
      {articles.length > 1 && (
        <nav style={{ minWidth: 160, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {articles.map((a) => (
            <button
              key={a.slug}
              onClick={() => setActiveSlug(a.slug)}
              style={{
                textAlign: 'left',
                padding: '6px 10px',
                border: 'none',
                borderRadius: 4,
                background: activeSlug === a.slug ? '#e5f0ff' : 'transparent',
                cursor: 'pointer',
                fontWeight: activeSlug === a.slug ? 600 : 400,
                fontSize: 13,
              }}
            >
              {a.title}
            </button>
          ))}
        </nav>
      )}
      {/* Article body */}
      {active && (
        <div style={{ flex: 1, minWidth: 240 }}>
          <h4 style={{ margin: '0 0 8px', fontSize: 15 }}>{active.title}</h4>
          <p style={{ margin: '0 0 12px', fontSize: 13, color: '#555' }}>{active.summary}</p>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'inherit',
              fontSize: 13,
              lineHeight: 1.6,
              margin: 0,
            }}
          >
            {active.body}
          </pre>
        </div>
      )}
    </div>
  );

  if (!triggerLabel) return panelContent;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{
          background: 'none',
          border: 'none',
          color: '#2563eb',
          cursor: 'pointer',
          fontSize: 13,
          padding: 0,
          textDecoration: 'underline',
        }}
      >
        {triggerLabel}
      </button>

      {open && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={(e) => e.target === e.currentTarget && setOpen(false)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 28,
              maxWidth: 600,
              width: '90vw',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 18 }}>Help</h3>
              <button
                onClick={() => setOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 20,
                  cursor: 'pointer',
                  color: '#666',
                }}
              >
                ✕
              </button>
            </div>
            {panelContent}
          </div>
        </div>
      )}
    </>
  );
};

export default HelpPanel;
// CB:END help-panel
